import React from 'react'
import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: 'website' | 'article'
  author?: string
  publishedTime?: string
  modifiedTime?: string
  noindex?: boolean
  structuredData?: object
}

const SEO: React.FC<SEOProps> = ({
  title = 'MortgageFlow.pro - Professional Mortgage Broker CRM & Pipeline Management Software',
  description = 'Complete mortgage broker software solution with AI-powered automation, deal pipeline management, client CRM, task scheduling, and conversation tracking. Built specifically for mortgage professionals.',
  keywords = 'mortgage broker software, mortgage CRM, loan origination system, mortgage pipeline management, mortgage broker CRM, mortgage automation, mortgage lead management, mortgage task management, broker tools, mortgage technology',
  image = 'https://mortgageflow.pro/og-image.jpg',
  url = 'https://mortgageflow.pro',
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  noindex = false,
  structuredData
}) => {
  const siteTitle = 'MortgageFlow.pro'
  const fullTitle = title === siteTitle ? title : `${title} | ${siteTitle}`

  // Default structured data for SaaS product
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'MortgageFlow',
    applicationCategory: 'BusinessApplication',
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: '29',
      highPrice: '149',
      priceValidUntil: '2027-12-31'
    },
    operatingSystem: 'Web, iOS, Android',
    description: description,
    url: url,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '127'
    }
  }

  const finalStructuredData = structuredData || defaultStructuredData

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="index,follow" />}
      <link rel="canonical" href={url} />

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:creator" content="@mortgageflow" />

      {/* Article-specific tags */}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      {/* Additional SEO tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />
      <meta name="author" content="MortgageFlow" />

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </Helmet>
  )
}

export default SEO
